import { theme as antTheme } from "antd";

import { buildAntThemeConfig } from "./antTheme";
import type { ResolvedTheme } from "./theme";

/**
 * Writes the accent/status `--axl-*` tokens onto <html> from antd's own design tokens for the school's
 * colour and the current theme. antd's css variables live on component roots, not on :root, so our SCSS
 * cannot `var(--ant-color-primary)` from arbitrary places — this mirrors them once per change instead.
 * `primary` is the accent of THIS theme (design `brand.darkPrimaryColor` in the dark one, when set);
 * `buttonColor` (design `brand.buttonColor`) gives primary buttons their own `--axl-btn*` (default: the accent).
 */
export function applyAccentTokens(
  resolved: ResolvedTheme,
  primary?: string | null,
  buttonColor?: string | null,
  liftDark = false,
) {
  const base = antTheme.getDesignToken(buildAntThemeConfig(resolved, primary));
  // the accent of text, icons and highlights; in the dark theme lifted until it reads on the surface (see liftedDarkTokens)
  const t = resolved === "dark" && liftDark ? liftedDarkTokens(primary, base) : base;
  const b = buttonColor ? antTheme.getDesignToken(buildAntThemeConfig(resolved, buttonColor)) : base;
  const style = document.documentElement.style;
  const set = (name: string, value: string) => style.setProperty(name, value);

  set("--axl-primary", t.colorPrimary);
  set("--axl-primary-hover", t.colorPrimaryHover);
  set("--axl-primary-active", t.colorPrimaryActive);
  set("--axl-primary-soft", t.colorPrimaryBg);
  set("--axl-primary-soft-hover", t.colorPrimaryBgHover);
  set("--axl-primary-border", t.colorPrimaryBorder);
  set("--axl-primary-text", t.colorPrimaryText);
  set("--axl-on-primary", base.colorTextLightSolid);
  set("--axl-btn", b.colorPrimary);
  set("--axl-btn-hover", b.colorPrimaryHover);
  set("--axl-btn-active", b.colorPrimaryActive);
  set("--axl-btn-soft", b.colorPrimaryBg);
  set("--axl-on-btn", b.colorTextLightSolid);
  set("--axl-success", t.colorSuccess);
  set("--axl-success-soft", t.colorSuccessBg);
  set("--axl-warning", t.colorWarning);
  set("--axl-warning-soft", t.colorWarningBg);
  set("--axl-danger", t.colorError);
  set("--axl-danger-soft", t.colorErrorBg);
}

type AccentTokens = ReturnType<typeof antTheme.getDesignToken>;

const DARK_SURFACE = "#15181e"; // `SURFACES.dark.container`
const MIN_CONTRAST = 4.5;

const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const mixWithWhite = (hex: string, w: number) =>
  "#" + rgb(hex).map((c) => Math.round(c + (255 - c) * w).toString(16).padStart(2, "0")).join("");
const luminance = (hex: string) => {
  const [r, g, b] = rgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
};
const contrast = (a: string, b: string) => {
  const [x, y] = [luminance(a), luminance(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
};

/**
 * Dark theme without the school's own dark accent: antd's dark ramp makes the accent DARKER than the light one
 * (violet #7c3aed → #6c34cc, 2.5:1 on the dark surface), so accent text, icons and links barely read. Lift the
 * seed towards white until the dark-ramp accent reaches 4.5:1 on the surface; buttons keep the base accent.
 */
/** The lifted seed: antd's dark ramp of it reads at 4.5:1 on the dark surface. Pass it as a seed (e.g. `colorLink`). */
export function liftedDarkSeed(primary: string | null | undefined, fallback: string): string {
  const seed = /^#[0-9a-f]{6}$/i.test(primary ?? "") ? primary! : fallback;
  if (!/^#[0-9a-f]{6}$/i.test(seed)) return seed;
  for (let w = 0.1; w <= 0.6001; w += 0.05) {
    const lifted = mixWithWhite(seed, w);
    const t = antTheme.getDesignToken(buildAntThemeConfig("dark", lifted));
    if (/^#[0-9a-f]{6}$/i.test(t.colorPrimary) && contrast(t.colorPrimary, DARK_SURFACE) >= MIN_CONTRAST) return lifted;
  }
  return mixWithWhite(seed, 0.6);
}

export function liftedDarkTokens(primary: string | null | undefined, base: AccentTokens): AccentTokens {
  return antTheme.getDesignToken(buildAntThemeConfig("dark", liftedDarkSeed(primary, base.colorPrimary)));
}
